import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'reactstrap'
import timeTrackApi from 'services/timeTrackApi'
import { ITimetrackInterface, TimeTrackCreate } from 'Types/TimeTrack/ITimeTrack'
import Track from './Track'
import styles from 'components/private/home/timetracks/timetrack.module.scss'
import trackStyles from './track.module.scss'
import addIcon from 'assets/img/add-icon.svg'
import DeleteModal from 'components/ui/modals/DeleteModal'
import { SnackbarContext } from 'components/ui/snackbar/context/Snackbar'
import TimeTrackCreateModal from './TimeTrackCreateModal'
import TimeTrackSkeleton from './TimeTrackSkeleton'
import { LocalStorageKeyEnum } from 'localStorage.enum'
import GenericContentPage from 'components/ui/genericContentPage/GenericContentPage'
import { sortTimeTracks } from './utils/SortTimetracks'
import { MonthSelector } from 'components/ui/monthSelector/MonthSelector'
import { Filter } from 'Types/Filter'
import moment from 'moment'
import holidayApi from 'services/holidayApi'
import { IHoliday } from 'Types/Holidays/IHoliday'
import { normalizeDate } from 'Helpers/date'
import WorkingDays from './workingDays/WorkingDays'
import TimeOff from './timeOff/TimeOff'
import MonthOverview from './monthOverview/MonthOverview'
import absenceApi from 'services/absenceApi'
import { Absence } from 'Types/Absences/Absence'
import { getMonthRange } from './utils/absenceDays'

const TimeTracker: React.FC = () => {
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [sortedTimeTracks, setSortedTimeTracks] = useState<Array<ITimetrackInterface[]>>([])
  const [lastTimeTrack, setLastTimeTrack] = useState<ITimetrackInterface>()
  const [timeTrackData, setTimeTrackData] = useState<ITimetrackInterface>()
  const [createModal, setCreateModal] = useState<boolean>(false)
  const snackbarContext = useContext(SnackbarContext)
  const [timeTrack, setTimeTrack] = useState<TimeTrackCreate>()
  const [loading, setLoading] = useState<boolean>(true)
  const [loadingHolidays, setLoadingHolidays] = useState<boolean>(true)
  const [workingHours, setWorkingHours] = useState<number>()
  const [filters, setFilters] = useState<Filter[]>([])
  const [user, setUser] = useState<string>(
    localStorage.getItem(LocalStorageKeyEnum.USERID)!.toString()
  )
  const [holidays, setHolidays] = useState<IHoliday[] | undefined>()
  const [holidayStartDate, setHolidayStartDate] = useState(
    normalizeDate(moment().startOf('month').toISOString())
  )
  const [holidayEndDate, setHolidayEndDate] = useState(
    normalizeDate(moment().endOf('month').toISOString())
  )
  const [absences, setAbsences] = useState<Absence[]>([])
  const [loadingAbsences, setLoadingAbsences] = useState<boolean>(true)
  const { monthStart, monthEnd } = useMemo(() => getMonthRange(filters), [filters])
  const entryCount = sortedTimeTracks.reduce((count, day) => count + day.length, 0)

  useEffect(() => {
    const getAbsencesForMonth = async () => {
      try {
        setLoadingAbsences(true)
        setAbsences(await absenceApi.getAbsencesForUserInPeriod(user, monthStart, monthEnd))
      } catch (e) {
        snackbarContext?.setShowSnackbarData(true, (e as any).message, 'danger')
      } finally {
        setLoadingAbsences(false)
      }
    }
    getAbsencesForMonth()
  }, [user, monthStart, monthEnd])

  useEffect(() => {
    getAllHolidaysForCurrentMonth()
  }, [holidayStartDate])

  useEffect(() => {
    if (filters.length > 0) {
      let userId = filters.find((el) => el.key === 'userId')
        ? filters.find((el) => el.key === 'userId')?.value
        : localStorage.getItem(LocalStorageKeyEnum.USERID)!.toString()
      setUser(userId!)
      let startDate = normalizeDate(
        moment(filters.find((el) => el.key === 'startDate')?.value).toISOString() ??
          moment().startOf('month').toISOString()
      )
      let endDate = normalizeDate(
        moment(filters.find((el) => el.key === 'endDate')?.value).toISOString() ??
          moment().endOf('month').toISOString()
      )
      setHolidayStartDate(startDate)
      setHolidayEndDate(endDate)
      userId && startDate && endDate && getAllTimeTrackForUser(userId, startDate, endDate)
    }
  }, [filters])

  async function getAllHolidaysForCurrentMonth() {
    try {
      setLoadingHolidays(true)
      const response = await holidayApi.getAllHolidaysBetweenDates(holidayStartDate, holidayEndDate)
      setHolidays(response)
    } catch (e) {
      snackbarContext?.setShowSnackbarData(true, (e as any).message, 'danger')
    } finally {
      setLoadingHolidays(false)
    }
  }

  async function getAllTimeTrackForUser(userId: string, startDate: string, endDate: string) {
    try {
      setLoading(true)
      var response = await timeTrackApi.getAllTimeTrackForUser(userId, startDate, endDate)
      const tempLastTimetrack = response.timeTrack[response.timeTrack.length - 1]
      tempLastTimetrack &&
        setLastTimeTrack({
          id: tempLastTimetrack.id,
          description: tempLastTimetrack.description,
          workingHours: tempLastTimetrack.workingHours,
          chargeableHours: tempLastTimetrack.chargeableHours,
          projectRole: tempLastTimetrack.projectRole,
          project: tempLastTimetrack.project,
          startDate: tempLastTimetrack.date,
          endDate: tempLastTimetrack.date,
          ignoreHoliday: false,
        })
      var timeTracksArray = response.timeTrack
        ? sortTimeTracks(
            response.timeTrack.map((t) => ({
              id: t.id,
              description: t.description,
              workingHours: t.workingHours,
              chargeableHours: t.chargeableHours,
              projectRole: t.projectRole,
              project: t.project,
              startDate: t.date,
              endDate: t.date,
              ignoreHoliday: false,
            }))
          )
        : []
      setSortedTimeTracks(timeTracksArray)
      setWorkingHours(response.timePeriodWorkingHoursCount)
    } catch (e) {
      snackbarContext?.setShowSnackbarData(true, (e as any).message, 'danger')
    } finally {
      setLoading(false)
    }
  }

  async function deleteTimeTrack(timeTrackId: number) {
    try {
      setLoading(true)
      await timeTrackApi.deleteTimeTrack(timeTrackId)
      let userId = filters.find((el) => el.key === 'userId')
        ? filters.find((el) => el.key === 'userId')?.value
        : localStorage.getItem(LocalStorageKeyEnum.USERID)?.toString()
      let startDate =
        filters.find((el) => el.key === 'startDate')?.value ?? moment().format('YYYY-MM-DD')
      let endDate =
        filters.find((el) => el.key === 'endDate')?.value ?? moment().format('YYYY-MM-DD')
      startDate = normalizeDate(moment(startDate).toISOString())
      endDate = normalizeDate(moment(endDate).toISOString())
      setHolidayStartDate(startDate)
      setHolidayEndDate(endDate)
      await getAllTimeTrackForUser(
        userId ?? localStorage.getItem(LocalStorageKeyEnum.USERID)!.toString(),
        startDate,
        endDate
      )
      snackbarContext?.setShowSnackbarData(true, 'TimeTrack deleted', 'success')
    } catch (e) {
      snackbarContext?.setShowSnackbarData(true, (e as any).message, 'danger')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container>
      {/* generic content page sets same header layout and renders different content as React FC */}
      <GenericContentPage
        header={{
          title: 'Time Tracker',
          buttonFunc: setCreateModal,
          buttonTitle: 'Add Entry',
          icon: addIcon,
        }}
      >
        {loading || loadingHolidays ? (
          <>
            <MonthSelector monthActive={true} filters={filters} setFilters={setFilters} />
            <TimeTrackSkeleton />
          </>
        ) : (
          <>
            <MonthSelector monthActive={true} filters={filters} setFilters={setFilters} />
            <MonthOverview
              workingHours={workingHours}
              holidays={holidays}
              filters={filters}
              absences={absences}
            />
            <Row>
              <Col xs={12} lg={7} className={styles.cardCol}>
                <WorkingDays
                  timetracks={sortedTimeTracks}
                  holidays={holidays}
                  filters={filters}
                  absences={absences}
                  loading={loadingAbsences}
                />
              </Col>
              <Col xs={12} lg={5} className={styles.cardCol}>
                <TimeOff
                  absences={absences}
                  holidays={holidays}
                  filters={filters}
                  loading={loadingAbsences}
                />
              </Col>
            </Row>
            <div className={trackStyles.sectionHeader}>
              <h4>Submitted entries</h4>
              <span>
                {entryCount} {entryCount === 1 ? 'entry' : 'entries'} · {workingHours ?? 0}h
              </span>
            </div>
            {sortedTimeTracks.length === 0 ? (
              <div className={trackStyles.empty}>No entries for this month yet.</div>
            ) : (
              <div className={trackStyles.days}>
                {sortedTimeTracks.map((timetrack) => (
                  <Track
                    key={timetrack[0].id}
                    loading={loading}
                    timetracks={timetrack}
                    setDeleteModal={setDeleteModal}
                    setDeleteData={setTimeTrackData}
                    setCreateModal={setCreateModal}
                    setTimeTrack={setTimeTrack}
                  />
                ))}
              </div>
            )}
          </>
        )}

        <TimeTrackCreateModal
          isOpen={createModal}
          setCreateModal={setCreateModal}
          timetrack={timeTrack}
          timetracks={sortedTimeTracks}
          lastTimetrack={lastTimeTrack}
          setTimeTrack={setTimeTrack}
          getUserTimeTracks={getAllTimeTrackForUser}
          userId={user}
          isEdit={!!timeTrack}
        />

        <DeleteModal
          isOpen={deleteModal}
          setDeleteModal={setDeleteModal}
          setDeleteData={setTimeTrackData}
          data={timeTrackData}
          delete={deleteTimeTrack}
        />
      </GenericContentPage>
    </Container>
  )
}

export default TimeTracker
